import { useCallback, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';

import { EMPTY_HISTORY, pushHistory, redoHistory, undoHistory } from '../utils/editHistory';

/**
 * Undo/redo over several pieces of state that change together.
 *
 * Edits go through `commit` rather than an effect watching the state: under React 17 an update from
 * a SortableJS callback or a document listener isn't batched, so an effect would record the
 * half-applied render between one setter and the next. `commit` writes them all in one batch.
 *
 * @param {object} present - The current values, keyed as `setters` is
 * @param {object} setters - A state setter for each key of `present`
 * @param {string[]} edits - The keys whose change counts as an edit; the rest (a cursor) are only
 *   restored along with them
 */
const useEditHistory = (present, setters, edits = Object.keys(present)) => {
  const [history, setHistory] = useState(EMPTY_HISTORY);

  // Read at call time, so the callbacks below can be stable and still see this render's values
  const latest = useRef();
  latest.current = { present, setters, edits, history };

  const apply = (snapshot, nextHistory) => {
    // Ahead of the render, for a second commit in the same handler
    latest.current.present = snapshot;
    latest.current.history = nextHistory;

    batchedUpdates(() => {
      Object.entries(snapshot).forEach(([key, value]) => latest.current.setters[key](value));
      setHistory(nextHistory);
    });
  };

  /**
   * Applies an edit. `next` holds only the keys that change, or is a function of the current values
   * returning them. With `replace`, the edit stands in for the last one rather than stacking on it.
   */
  const commit = useCallback((next, { replace = false } = {}) => {
    const { present: current, edits: keys, history: past } = latest.current;
    const changes = typeof next === 'function' ? next(current) : next;
    const isEdit = keys.some((key) => key in changes && changes[key] !== current[key]);

    apply({ ...current, ...changes }, isEdit && !replace ? pushHistory(past, current) : past);
  }, []);

  const undo = useCallback(() => {
    const step = undoHistory(latest.current.history, latest.current.present);

    if (step) apply(step.snapshot, step.history);
  }, []);

  const redo = useCallback(() => {
    const step = redoHistory(latest.current.history, latest.current.present);

    if (step) apply(step.snapshot, step.history);
  }, []);

  const reset = useCallback(() => {
    latest.current.history = EMPTY_HISTORY;
    setHistory(EMPTY_HISTORY);
  }, []);

  return { commit, undo, redo, reset, canUndo: history.past.length > 0, canRedo: history.future.length > 0 };
};

export default useEditHistory;
